import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { makeStyles } from "helpers/StyleHelper";
import useBoolean from "hooks/useBoolean";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { useIntl } from "react-intl";
import EnhancedTableHead from "./EnhancedTableHead";
import EnhancedTableRow from "./EnhancedTableRow";
import EnhancedTableToolbar from "./EnhancedTableToolbar";

export const MAX_ROWS_BEFORE_PAGINATION = 10;
export const DEFAULT_ROWS_PER_PAGE = 10;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      width: "100vw",
      boxShadow: "none",
    },
  },
}));

export type LazyTableRow = {
  id: string;
  isExpandable?: boolean;
  renderExpandedData?(): JSX.Element;
  color?: string;
};

export type TableSortType = "desc" | "asc";

export type BasicCellType = {
  accessor: string;
  customSortAccessor?: string;
  label: string;
  align?: "left" | "center" | "right";
  type: "text" | "node" | "boolean" | "date" | "datetime" | "price";
  className?: string;
  width?: any;
  noSort?: boolean;
};

export type BooleanCellType = BasicCellType & {
  type: "boolean";
  TrueComponent?: React.ReactNode;
};

export type NodeCellType = BasicCellType & {
  type: "node";
  View: React.FC<{ value: any }>;
};

export type TableCellType = BasicCellType | BooleanCellType | NodeCellType;

/**
 * One action the selection toolbar offers for the selected rows.
 *
 * `dialogLabel` is the confirmation text, and omitting it runs the action straight away - which is what a
 * read-only action such as a download wants. It is passed to `ConfirmDialog` as a message id, so an id is the
 * intended form; already-translated text also works, because `FormattedMessage` renders an unknown id as
 * itself, and `RequestsList` and `WebhooksList` both rely on that. Either way it must not be assumed to be
 * one or the other.
 *
 * An action that runs straight away has no dialog to render a failure in, so pass `onError` to report one -
 * without it a rejection is rethrown and only reaches the browser's unhandled rejection handler.
 */
export type ExecuteMultipleAction = {
  dialogLabel?: string;
  onExecuteMultiple: (ids: string[]) => Promise<void>;
  onError?: (error: unknown) => void;
  buttonLabel: string;
  /**
   * Whether this action can act on the current selection. Omitting it keeps the button enabled whenever
   * anything is selected, which is what every caller without a per-row rule expects.
   */
  isEnabledFor?: (selectedIds: string[]) => boolean;
};

export type LazyTableProps = {
  topComp?: React.ReactNode;
  title?: string;
  titleComponent?: React.ReactNode;
  cells: Array<TableCellType>;
  rows: Array<LazyTableRow>;
  rowCount: number;
  loading?: boolean;
  allowPagination?: boolean;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  order: TableSortType;
  setOrder: (direction: TableSortType) => void;
  orderBy: string;
  setOrderBy: (column: string) => void;
  dense?: boolean;
  className?: string;
  allowDragAndDrop?: boolean;
  onDragAndDropEnd?: (result: DropResult) => void;
  onEdit?: (id: string) => void;
  onClick?: (id: string, row: LazyTableRow) => void;
  onDelete?: (ids: string[]) => void;
  /** A single action, or several: the toolbar renders one button per action. */
  executeMultiple?: ExecuteMultipleAction | ExecuteMultipleAction[];
  /** Overrides the "# selected" counter message. Defaults to `tableNumSelected`. */
  numSelectedMessageId?: string;
  useRowIndexesAsReactKey?: boolean;
  collapsable: boolean;
  hideEmptyRows?: boolean;
};

const ExpandableTable = ({
  topComp,
  title,
  titleComponent,
  cells,
  loading,
  rows,
  rowCount,
  allowPagination = true,
  page,
  setPage,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  rowsPerPage,
  setRowsPerPage,
  dense,
  className,
  allowDragAndDrop,
  onDragAndDropEnd,
  onEdit,
  onDelete,
  onClick,
  executeMultiple,
  numSelectedMessageId,
  useRowIndexesAsReactKey,
  collapsable,
  hideEmptyRows,
}: LazyTableProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dragging, setDragging] = useBoolean(false);
  const [data, setData] = useState<LazyTableRow[]>([]);
  const handleRequestSort = (event: any, property: string) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder!(isDesc ? "asc" : "desc");
    setOrderBy!(property);
  };

  useEffect(() => {
    setData(rows);
  }, [rows]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const innerOnDelete = (ids: string[]) => {
    if (onDelete) onDelete(ids);
    setSelectedIds([]);
  };

  // Memoised on the prop itself so a consumer passing an inline object keeps the identity it has today,
  // and nothing downstream of it starts or stops memoising as a side effect of accepting an array.
  const executeMultipleActions = useMemo(
    () =>
      (Array.isArray(executeMultiple)
        ? executeMultiple
        : executeMultiple
        ? [executeMultiple]
        : []
      ).filter((action) => !!action?.onExecuteMultiple),
    [executeMultiple]
  );

  const toolbarActions = useMemo(
    () =>
      executeMultipleActions.map((action) => ({
        ...action,
        onExecuteMultiple: async (ids: string[]): Promise<void> => {
          await action.onExecuteMultiple(ids);
          setSelectedIds([]);
        },
      })),
    [executeMultipleActions]
  );

  const handleClick = useCallback(
    (event: any, id: string, row: LazyTableRow) => {
      if (onClick && event.target.type !== "checkbox" && !event.target.href)
        onClick(id, row);
      if (!onEdit && !onDelete && executeMultipleActions.length === 0) return;
      const selectedIndex = selectedIds.indexOf(id);
      const newSelected =
        selectedIndex === -1
          ? [...selectedIds, id]
          : selectedIds.filter((selectedId) => selectedId !== id);

      setSelectedIds(newSelected);
    },
    [onClick, onDelete, onEdit, executeMultipleActions, selectedIds]
  );

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = useCallback(
    (id: string) => selectedIds.indexOf(id) !== -1,
    [selectedIds]
  );

  const reorder = (
    list: LazyTableRow[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const emptyRows =
    !hideEmptyRows && allowPagination
      ? rowsPerPage! - Math.min(rowsPerPage!, rowCount - page! * rowsPerPage!)
      : 0;

  const onDragEnd = useCallback(
    async (result: any) => {
      // the only one that is required
      if (!result.destination) {
        return;
      }

      setDragging.setTrue();
      setData((prev) =>
        reorder(prev, result.source.index, result.destination.index)
      );

      try {
        onDragAndDropEnd && (await onDragAndDropEnd(result));
      } catch (error) {
        console.log(error);
      } finally {
        setDragging.setFalse();
      }
    },
    [onDragAndDropEnd, setDragging]
  );

  return (
    <Box className={className} sx={styles.root}>
      <Paper sx={styles.paper}>
        {topComp}
        {(title || titleComponent) && (
          <EnhancedTableToolbar
            title={title}
            titleComponent={titleComponent}
            selectedIds={selectedIds}
            onEdit={onEdit}
            onDelete={innerOnDelete}
            executeMultiple={toolbarActions}
            numSelectedMessageId={numSelectedMessageId}
          />
        )}
        {(dragging || loading) && (
          <LinearProgress
            style={{ width: "100%" }}
            color="primary"
            variant="query"
          />
        )}
        <TableContainer>
          <Table size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              cells={cells}
              numSelected={selectedIds.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={
                (onDelete || executeMultipleActions.length > 0) ? handleSelectAllClick : undefined
              }
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              collapsable={collapsable}
            />
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId="droppable-1"
                isDropDisabled={dragging || (!allowDragAndDrop ?? true)}
              >
                {(provided, snapshot) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {data.map((row, index) => (
                      <Draggable
                        key={useRowIndexesAsReactKey ? index : row.id}
                        draggableId={row.id}
                        index={index}
                        isDragDisabled={dragging || (!allowDragAndDrop ?? true)}
                      >
                        {(provided, snapshot) => (
                          <EnhancedTableRow
                            key={useRowIndexesAsReactKey ? index : row.id}
                            index={index}
                            row={row}
                            hover={!!(onEdit || onDelete || onClick)}
                            cells={cells}
                            withCheckbox={
                              !!onDelete ||
                              !!onEdit ||
                              executeMultipleActions.length > 0
                            }
                            handleClick={handleClick}
                            isSelected={isSelected}
                            innerRef={provided.innerRef}
                            draggableProps={provided.draggableProps}
                            dragHandleProps={provided.dragHandleProps}
                            collapsable={collapsable}
                          />
                        )}
                      </Draggable>
                    ))}
                    {emptyRows > 0 && (
                      <TableRow
                        style={{ height: (dense ? 33 : 53) * emptyRows }}
                      >
                        <TableCell colSpan={cells.length + 1} />
                      </TableRow>
                    )}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </TableContainer>

        {allowPagination && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rowCount}
            rowsPerPage={rowsPerPage!}
            page={page!}
            labelRowsPerPage={t({ id: "tableRowsPerPage" })}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default ExpandableTable;
